import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import { LynkLockup } from '../../../components/ui/LynkLogo';

/**
 * Hero — the title page of a printed proceedings volume.
 *
 * The previous hero was a near-black field with a blueprint grid, an
 * architectural pillar drawn in SVG and a hand-drawn squiggle under the
 * headline. Three problems, and they compound:
 *
 *   1. It was a dark block bolted onto a product that is now parchment and
 *      letterpress throughout. You met the app twice — once as a tech landing
 *      page, then again as something entirely different after signing in.
 *   2. The decoration carried no information. A blueprint grid and a Doric
 *      column say "architecture", and Lynk is not architecture software.
 *   3. It buried the mark. The lockup is the strongest thing the brand owns —
 *      Old Standard TT at 400 with the tagline set between two hairlines — and
 *      the old hero didn't show it at all.
 *
 * So: no imagery. The page opens the way a journal opens — rules, the mark,
 * the title, nothing else. Deliberately no kicker above the rule either: an
 * all-caps label announcing the category reads like an annotation on a design
 * spec, not like a product that has shipped.
 *
 * Everything here is type and 1px rules. Nothing is an image, so it stays
 * crisp at any zoom, recolours with the theme, and translates.
 */
const HeroSection = ({ onGetStarted, onBrowseConferences, isAuthenticated }) => {
  const { t } = useTranslation('conferences');
  const { t: tc } = useTranslation('common');

  return (
    <section className="relative bg-background">
      {/* Margin rules. On a letterpress page the type block is registered
          against the trim; these are that, and they're the only ornament.
          Hidden below xl where there is no margin to register against. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 start-[6%] w-px bg-border hidden xl:block" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 end-[6%] w-px bg-border hidden xl:block" />

      <div className="page-shell px-4 sm:px-6 lg:px-8">
        {/* Top padding is exactly one lg-button height (2.75rem) less than it
            was — 5rem → 2.25rem, and 7rem → 4.25rem at md. That is enough to
            pull the two calls to action above the fold on a laptop without
            touching the rhythm below the mark, which is what carries the
            title-page feel. Bottom padding is unchanged on purpose: cutting
            both would just move the crowding to the join with the next
            section. */}
        <div className="max-w-3xl mx-auto text-center pt-9 pb-14 md:pt-[4.25rem] md:pb-20">
          {/* Double rule, then the mark. A title page states who published it
              before it states what it is. */}
          <div aria-hidden="true" className="mb-1 h-px bg-border" />
          <div aria-hidden="true" className="mb-10 h-px bg-border" />

          <div className="flex justify-center text-mark">
            <LynkLockup size={112} />
          </div>

          <div aria-hidden="true" className="mt-10 mb-1 h-px bg-border" />
          <div aria-hidden="true" className="mb-10 h-px bg-border" />

          {/* The title. Old Standard TT 700 — well above its 18px floor. */}
          <h1 className="font-heading font-bold text-foreground text-[2rem] sm:text-[2.6rem] lg:text-[3.1rem] leading-[1.12] tracking-[-0.005em]">
            {t('landing.hero.titleLine1')}{' '}
            <span className="italic font-academic font-normal">
              {t('landing.hero.titleLine2')}
            </span>
          </h1>

          {/* Standfirst in EB Garamond — the reading face, at reading size. */}
          <p className="mt-6 font-academic text-[17px] sm:text-[19px] leading-[1.7] text-muted-foreground max-w-2xl mx-auto">
            {t('landing.hero.subtitle')}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Button size="lg" onClick={onGetStarted}>
              {isAuthenticated ? t('landing.hero.createConference') : t('landing.hero.getStartedFree')}
            </Button>
            {/* Outline, not a second solid fill — one main action per view. */}
            <Button variant="outline" size="lg" onClick={onBrowseConferences}>
              {t('landing.hero.browseConferences')}
            </Button>
          </div>

          {/* Colophon line: the three facts, separated by rules rather than
              tick icons. Icons here would be the third visual language on a
              page that only needs one. */}
          <ul className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] text-muted-foreground">
            {['freeToStart', 'secureReliable', 'trustedByResearchers'].map((key, i) => (
              <li key={key} className="flex items-center gap-5">
                {i > 0 && <span aria-hidden="true" className="w-px h-3.5 bg-border" />}
                <span>{t(`landing.hero.features.${key}`)}</span>
              </li>
            ))}
          </ul>

          <p className="sr-only">{tc('brand.tagline')}</p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
